import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { personCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import { AddressIcon, EmailIcon, EoriIcon, PhoneIcon, PinIcon } from 'components/Shared/Icons/tooltip_icons';
import { Person } from 'types/entities/types';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditPeople from './AddOrEditPeople';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Person;
  showActionButtons: boolean;
  onPersonDelete?: () => void;
  onPersonUpdate?: () => void;
}

const PersonCard = (props: Props) => {
  const { option: person, showActionButtons = true, onPersonDelete, onPersonUpdate } = props;

  const [editPersonModalOpen, setEditPersonModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deletePerson = () => {
    axiosService
      .delete(`/entities/person/${person.id}/`)
      .then(() => {
        if (onPersonDelete) {
          onPersonDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onPersonEdit = () => {
    if (onPersonUpdate) {
      onPersonUpdate();
    }
    setEditPersonModalOpen(false);
  };

  return (
    <>
      <Card elevation={1} sx={personCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={personCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditPersonModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={personCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={`${person.first_name} ${person.last_name}`} tooltipText={person.id} />}
          subheader="Person"
          sx={personCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={personCardStyles.contactBox}>
            <EmailIcon />
            <Typography color="text" sx={personCardStyles.contactValue}>
              {person.email || '-'}
            </Typography>
          </Box>
          <Box sx={personCardStyles.contactBox}>
            <PhoneIcon />
            <Typography color="text" sx={personCardStyles.contactValue}>
              {person.phone || '-'}
            </Typography>
          </Box>
          <Box sx={personCardStyles.contactBox}>
            <AddressIcon />
            <Typography color="text" sx={personCardStyles.contactValue}>
              {person.address || '-'}
            </Typography>
          </Box>
          <Box sx={personCardStyles.contactBox}>
            <PinIcon />
            <Typography color="text" sx={personCardStyles.contactValue}>
              {person.pin || '-'}
            </Typography>
          </Box>
          <Box sx={personCardStyles.contactBox}>
            <EoriIcon />
            <Typography color="text" sx={personCardStyles.contactValue}>
              {person.eori || '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete person</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete person{' '}
            <b>
              {person.first_name} {person.last_name}
            </b>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deletePerson} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditPeople
        person={person}
        onSave={onPersonEdit}
        modalTitle="Edit person"
        isModalOpen={editPersonModalOpen}
        setModalOpen={setEditPersonModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default PersonCard;
